import { Notification } from "@transfr-inc/dashboard-components";
import {
  formatDateDigits,
  formatTime,
} from "@transfr-inc/dashboard-components/utils";

import { ContentType } from "@transfr-inc/dashboard-components/curriculum";
import { listOfExemptedSims } from "./course.utils";

export const ActivityStatusNotification = ({
  status = "",
  contentType,
  masteryScore,
  completionDate,
  latestTimestamp,
  timeSpent,
  timeToComplete,
  isComplete,
  learningExperienceId,
}) => {
  const normalizedStatus =
    typeof status === "string"
      ? status.toUpperCase()
      : status?.name?.toUpperCase() ?? "";

  const masteryPercentage = masteryScore
    ? `${(masteryScore * 100).toFixed(0)}%`
    : "";
  const isAssessment = contentType === ContentType.Assessment.id;
  const formattedLatest = latestTimestamp ? formatDateDigits(latestTimestamp) : "";
  const formattedCompletion = completionDate ? formatDateDigits(completionDate) : "";

  switch (normalizedStatus) {
    case "PASSED": {
      return (
        <Notification className="mastered" icon={["fa-solid", "fa-check"]}>
          <div className="notification-text-score">
            <div className="body-text">
              <span className="bold">Passed</span> {formattedLatest}
            </div>
            {isAssessment && <div className="score-text">{masteryPercentage}</div>}
          </div>
        </Notification>
      );
    }
    case "FAILED": {
      return (
        <Notification className="needs-work" icon={["fa-solid", "fa-user-hard-hat"]}>
          <div className="notification-text-score">
            <div className="body-text">
              <span className="bold">Needs Work</span>
              {formattedLatest && ` as of ${formattedLatest}`}
            </div>
            {isAssessment && isComplete && (
              <div className="score-text">{masteryPercentage}</div>
            )}
          </div>
        </Notification>
      );
    }
    case "COMPLETE":
    case "MASTERED": {
      return (
        <Notification className="mastered" icon={["fa-solid", "fa-check"]}>
          <div className="notification-text-score">
            <div className="body-text">
              <span className="bold">
                {normalizedStatus === "MASTERED" ? "Mastered" : "Completed"}
              </span>{" "}
              {formattedCompletion + " "}
              {timeSpent
                ? `in ${formatTime(timeSpent, { format: "long", withSeconds: false })}`
                : ""}
            </div>
            {normalizedStatus === "MASTERED" && (
              <div className="score-text">{masteryPercentage}</div>
            )}
          </div>
        </Notification>
      );
    }
    case "COMPLETED": {
      return (
        <Notification className="mastered" icon={["fa-solid", "fa-check"]}>
          <div className="notification-text-score">
            <div className="body-text">
              <span className="bold">Done</span>
              {formattedLatest && ` ${formattedLatest}`}
            </div>
          </div>
        </Notification>
      );
    }
    case "IN PROGRESS": {
      return (
        <Notification className="needs-work" icon={["fa-solid", "fa-user-hard-hat"]}>
          <div className="body-text">
            {"In Progress"}{" "}
            {formattedLatest && `as of ${formattedLatest}`}
          </div>
        </Notification>
      );
    }
    case "WHEEL SPINNING":
    case "STUCK": {
      return (
        <Notification className="stuck" icon={["fa-solid", "fa-hand"]}>
          <div className="notification-text-score">
            <div className="body-text">
              Stuck {formattedLatest && `as of ${formattedLatest}`}
            </div>
            <div className="score-text">{masteryPercentage}</div>
          </div>
        </Notification>
      );
    }
    case "EXEMPT": {
      if (learningExperienceId && listOfExemptedSims.includes(learningExperienceId)) {
        return <div />;
      }
      const exemptDate = formattedCompletion || formattedLatest;
      return (
        <Notification className="mastered" icon={["fa-solid", "fa-check"]}>
          <div className="notification-text-score">
            <div className="body-text">
              <span className="bold">Exempt</span>
              {exemptDate && ` ${exemptDate}`}
            </div>
          </div>
        </Notification>
      );
    }
    default: {
      if (contentType === ContentType.CurriculumResources.id) {
        return (
          <Notification className="estimated" icon={["fa-solid", "fa-clock"]}>
            <div className="time-text">
              Open this document to mark it as complete
            </div>
          </Notification>
        );
      }
      if (contentType === ContentType.InHeadset.id) {
        return (
          <Notification className="estimated" icon={["fa-solid", "fa-clock"]}>
            <div className="time-text">
              Estimated time to complete is&nbsp;
              <span className="bold">
                {timeToComplete
                  ? formatTime(timeToComplete, { format: "long", withSeconds: false })
                  : "? minutes"}
              </span>
            </div>
          </Notification>
        );
      }
      return <></>;
    }
  }
};
